const DEFAULT_SCOPE_POPUP_MAX_WIDTH = 391;
const SCOPE_POPUP_INLINE_END_GUTTER = 24;
const MIN_SCOPE_POPUP_USABLE_WIDTH = 2 * SCOPE_POPUP_INLINE_END_GUTTER;

export type ScopePopupPlacement = 'bottom-end' | 'bottom-start';

export type ScopePopupSizingResult = {
	maxWidth: number;
	placement: ScopePopupPlacement;
};

const getPanelBoundary = (trigger: HTMLElement): HTMLElement | null =>
	trigger.closest<HTMLElement>('nav[data-layout-slot]') ??
	trigger.closest<HTMLElement>('[data-rovo-chat-panel]');

const getPlacement = (
	physicalAlignment: 'left' | 'right',
	isRtl: boolean,
	usesLogicalPlacement: boolean,
): ScopePopupPlacement => {
	if (usesLogicalPlacement) {
		const logicalStartAlignment = isRtl ? 'right' : 'left';
		return physicalAlignment === logicalStartAlignment ? 'bottom-start' : 'bottom-end';
	}

	// PopperJS treats start/end as physical edges, including in RTL.
	return physicalAlignment === 'left' ? 'bottom-start' : 'bottom-end';
};

/**
 * Keeps a scope popup inside the Rovo panel while preserving logical start alignment where there is
 * room. ADS navigation provides its own layout boundary; the legacy mini-modal uses the shared Rovo
 * Chat panel marker. The top-layer Popup uses logical placement, whereas legacy PopperJS uses
 * physical start/end placement, so the selected placement is returned with the maximum width.
 */
const getSizing = (trigger: HTMLElement, usesLogicalPlacement: boolean): ScopePopupSizingResult => {
	const isRtl = trigger.ownerDocument.defaultView?.getComputedStyle(trigger).direction === 'rtl';
	const preferredAlignment = isRtl ? 'right' : 'left';
	const panelBoundary = getPanelBoundary(trigger);
	if (!panelBoundary) {
		return {
			maxWidth: DEFAULT_SCOPE_POPUP_MAX_WIDTH,
			placement: getPlacement(preferredAlignment, isRtl, usesLogicalPlacement),
		};
	}

	const triggerRect = trigger.getBoundingClientRect();
	const panelRect = panelBoundary.getBoundingClientRect();
	const availableByAlignment = {
		left: Math.floor(panelRect.right - triggerRect.left - SCOPE_POPUP_INLINE_END_GUTTER),
		right: Math.floor(triggerRect.right - panelRect.left - SCOPE_POPUP_INLINE_END_GUTTER),
	};
	const alternateAlignment = preferredAlignment === 'left' ? 'right' : 'left';
	const physicalAlignment =
		availableByAlignment[preferredAlignment] >= MIN_SCOPE_POPUP_USABLE_WIDTH ||
		availableByAlignment[alternateAlignment] <= availableByAlignment[preferredAlignment]
			? preferredAlignment
			: alternateAlignment;
	const panelContainedFallback = Math.max(
		1,
		Math.floor(panelRect.width - 2 * SCOPE_POPUP_INLINE_END_GUTTER),
	);

	return {
		maxWidth:
			availableByAlignment[physicalAlignment] > 0
				? availableByAlignment[physicalAlignment]
				: panelContainedFallback,
		placement: getPlacement(physicalAlignment, isRtl, usesLogicalPlacement),
	};
};

const getMaxWidth = (trigger: HTMLElement): number => getSizing(trigger, true).maxWidth;

export const scopePopupSizing: {
	defaultMaxWidth: number;
	getMaxWidth: (trigger: HTMLElement) => number;
	getPanelBoundary: (trigger: HTMLElement) => HTMLElement | null;
	getSizing: (trigger: HTMLElement, usesLogicalPlacement: boolean) => ScopePopupSizingResult;
} = {
	defaultMaxWidth: DEFAULT_SCOPE_POPUP_MAX_WIDTH,
	getMaxWidth,
	getPanelBoundary,
	getSizing,
};
